import { Typography } from "@mui/material";
import { Stack } from "@mui/material";
import { TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { useTBContext } from "../Contexts/useTBStates";
import { FlightBadge, PreferenceBadge } from "./TeeSheetBadges";
import { isOpsActiveRegistration } from "@moment-golf/shared";

interface ReservesListProps {
    tabHeight?: number;
}

export const ReservesList = ({ tabHeight }: ReservesListProps) => {

    const {factor, appTheme,
        viewEvent,
        viewEventData, 
        editMode, 
        roundValue, 
      } = useTBContext();

    const [filteredReserves, setFilteredReserves] = useState<any[]>([]);
    const [filteredScoreCodes, setFilteredScoreCodes] = useState<any[]>([]);


    useEffect(() => {
        if (viewEventData.registrations && roundValue) {
            console.log('View Event Data', viewEventData.registrations);
            const filteredReserves = viewEventData.registrations
                .filter((registration: any) => isOpsActiveRegistration(registration))
                .filter((registration: any) => {
                    // Check if teeTimes exists and filter based on the conditions
                    const hasTeeTimes = registration.teeTimes && Array.isArray(registration.teeTimes);
                    
                    return (
                        !hasTeeTimes || // Include if teeTimes does not exist
                        registration.teeTimes.length === 0 || // Include if teeTimes is empty
                        !registration.teeTimes.find((teeTime: any) => 
                            teeTime.round === roundValue || (teeTime.teeTime === null || teeTime.teeTime === "")
                        )
                    );
                });

            const filteredScoreCodes =  Array.from(new Set(filteredReserves.map((registration: any) => {
                return registration.scoreCode;
            })));

            setFilteredScoreCodes(filteredScoreCodes);

            setFilteredReserves(filteredReserves);
        }
    }, [viewEventData.registrations, roundValue]);
    
    return (
        <Stack direction='column' spacing={0.25} sx={{ height: '100%', minHeight: 0, overflow: 'hidden' }}>
            <Typography sx={{ fontWeight: 500, textAlign: 'center', fontSize: 14*factor, flexShrink: 0 }}>Reserves</Typography>
            <Stack direction='row' spacing={1} alignItems='center' sx={{ flexShrink: 0 }}>
                <TextField 
                    variant='outlined' 
                    size='small' 
                    placeholder='Search...' 
                    onChange={(e) => {
                        const searchValue = e.target.value.toLowerCase();
                        const filteredReserves = viewEventData.registrations
                            .filter((registration: any) => isOpsActiveRegistration(registration))
                            .filter((registration: any) =>  
                                !registration.teeTimes.some((teeTime: any) => teeTime.round === roundValue) && 
                                (registration.playerName?.toLowerCase().includes(searchValue))
                            );

                        const filteredScoreCodes =  Array.from(new Set(filteredReserves.map((registration: any) => {
                            return registration.scoreCode;
                        })));

                        const otherReservesByScoreCode = viewEventData.registrations
                            .filter((registration: any) => isOpsActiveRegistration(registration))
                            .filter((registration: any) =>
                                filteredScoreCodes.includes(registration.scoreCode) && 
                                !filteredReserves.some((reserve: any) => reserve._id === registration._id)
                            );

                        setFilteredReserves([...filteredReserves, ...otherReservesByScoreCode]);
                        setFilteredScoreCodes(filteredScoreCodes);
                    }} 
                    sx={{
                        padding: 0,
                        "& .MuiInputBase-input": { fontSize: 12*factor, height: 12*factor, padding: 1 },
                        '& .MuiOutlinedInput-root': {padding: '0px 0px'},
                        '& input': {textAlign: 'left', lineHeight: 'normal'},
                        flex: 3,
                        width: '100%',
                    }}
                    InputProps={{
                        sx: {
                            fontSize: 12*factor,
                        },
                    }}
                />
            </Stack>
            <Stack direction='column' spacing={0.25} flex={1}
                sx={{
                    overflow: 'auto',
                    minHeight: 0,
                    height: tabHeight != null ? tabHeight - 150 * factor : undefined,
                }}
            >
                {filteredScoreCodes.map((scoreCode: any, index: number) => {

                    const codeReserves = filteredReserves.filter((score: any) => score.scoreCode === scoreCode);

                    return (
                    <Stack direction='column' spacing={0.25} key={`no-tee-time-${index}`}
                        sx={{ border: `1px dotted ${appTheme.palette.primary.light}`, cursor: editMode ? 'grab' : 'default',
                            backgroundColor: !editMode ? 'rgba(0,0,0,0.05)' : 'transparent',
                            borderRadius: 1, padding: '3px 5px',
                        }}
                        draggable={editMode}
                        onDragStart={(e) => {
                            e.dataTransfer.setData('text/plain', JSON.stringify(
                                filteredReserves.filter((score: any) => score.scoreCode === scoreCode)
                            ));
                        }}
                    >
                        <Stack direction='row' alignItems='center' justifyContent='space-between' spacing={0.5}>
                            <Typography 
                                key={`no-tee-time-${index}`} 
                                sx={{ fontSize: 12*factor, flex: 1, minWidth: 0 }} 
                            >
                                {codeReserves.map(score => score.playerName).join(', ') || 'No Results'}
                            </Typography>
                            {(codeReserves[0]?.division || codeReserves[0]?.flight || codeReserves[0]?.teeGroupFocusPreference) && (
                                <Stack direction='row' spacing={0.5} alignItems='center' sx={{ flexShrink: 0 }}>
                                    <FlightBadge
                                        division={codeReserves[0]?.division}
                                        flight={codeReserves[0]?.flight}
                                        divisionIndex={Math.max(0, viewEvent?.flighting?.divisions?.findIndex((d: any) => d.divisionName === codeReserves[0]?.division) ?? 0)}
                                        factor={factor}
                                    />
                                    <PreferenceBadge
                                        preference={codeReserves[0]?.teeGroupFocusPreference}
                                        factor={factor}
                                    />
                                </Stack>
                            )}
                        </Stack>
                        {codeReserves[0]?.teamName && <Typography 
                            sx={{ 
                                fontSize: 10*factor,
                                fontStyle: 'italic'
                            }} 
                        >
                            {codeReserves[0].teamName || 'Unnamed Team'}
                        </Typography>
                        }
                    </Stack>
                )
            })}
            </Stack>
        </Stack>
    )
}

